// #4625 — the one decision about whether to ask somebody to rate the app.
//
// Pure on purpose. Every rule about who is asked lives here and nowhere else,
// so each one can be tested on its own and the four trigger points stay dumb:
// they decide only WHEN a good moment happened, never WHO deserves it.
//
// See design-docs/review-prompts.md, "Who gets asked: the entry gate".

import { DateTime } from 'luxon';
import { WhenMomentUnreadable, mayHappenAgain } from './periodic-suppression';

/**
 * Why we are not asking. Named rather than a bare `false`, because "we chose
 * not to" and "we could not tell" are different answers and the difference
 * decides whether anybody should look into it.
 */
export enum NoPromptReason {
  /** A coach is looking at somebody else's account. Never prompt then. */
  CoachViewingClient = 'coach-viewing-client',
  /** Their deterministic bucket is at or above the configured rollout percent. */
  OutsideRollout = 'outside-rollout',
  /** Something went wrong in this session, so this is a bad moment to ask. */
  SomethingWentWrongThisSession = 'something-went-wrong-this-session',
  /** They told us something was wrong recently. */
  RecentlyReportedAProblem = 'recently-reported-a-problem',
  /** Already asked within the cap window. */
  AlreadyAskedRecently = 'already-asked-recently',
  /**
   * We have not found out yet whether they pay. Emphatically not "they do not
   * pay" — the subscription facts come off the CouchDB login result, and on a
   * warm resume or an offline start that result is not there yet, so there is
   * nothing to read rather than nothing to find. Same distinction as
   * `EngagementUnknown` below, and for the same reason: a chosen refusal about
   * a person sends whoever investigates to look at a subscription where
   * nothing is wrong.
   */
  SubscriptionUnknown = 'subscription-unknown',
  /** Not a paying subscriber at all. */
  NotPaying = 'not-paying',
  /** Paying nothing yet, because they are inside a free trial. */
  InFreeTrial = 'in-free-trial',
  /** Subscription is anything other than cleanly active (suspended, paused, cancelled). */
  SubscriptionNotCleanlyActive = 'subscription-not-cleanly-active',
  /** We have no first-paid date for them, so we cannot judge tenure. Fails closed. */
  TenureUnknown = 'tenure-unknown',
  /** They have paid, but for less than the required time. */
  TooNewlyPaying = 'too-newly-paying',
  /**
   * The workout history could not be READ. Emphatically not "they have logged
   * nothing" — a database read that failed and an empty log are different
   * facts, and treating the first as the second would ask somebody whose
   * device is broken.
   */
  EngagementUnknown = 'engagement-unknown',
  /** They have not trained in enough of the recent weeks. */
  NotTrainingConsistently = 'not-training-consistently',
  /** They train, but not lately. */
  NoRecentWorkout = 'no-recent-workout',
  /**
   * The current time itself was unusable, so nothing that depends on it could
   * be judged. Its own reason rather than borrowing `EngagementUnknown`,
   * because a named reason is something somebody acts on and that one would
   * send them to the workout-history read, where nothing is wrong.
   */
  ClockUnknown = 'clock-unknown',
}

/**
 * Which tenure rule let somebody through (#4816). Reported on
 * `review_prompt_attempted` so the two groups can be compared later.
 */
export enum TenureRule {
  /** Paying 8+ weeks and trained in 5 of the last 8. Phase 1's only rule. */
  EightWeek = 'eight-week',
  /** Paying 4+ weeks and trained in 3 of the last 4. Phase 2 only. */
  FourWeek = 'four-week',
}

/**
 * May we ask, or why not.
 *
 * A flat interface rather than a discriminated union, for the same reason
 * `resolveActionCapability` in the subscription-ops tooling is flat: this
 * package is not compiled with `strictNullChecks`, so a union discriminated on
 * a boolean literal does not narrow and every reader would need a cast.
 * `reason` is present exactly when `mayPrompt` is false.
 */
export interface ReviewPromptDecision {
  mayPrompt: boolean;
  reason?: NoPromptReason;
  /**
   * True when a rollout value was configured but could not be read. Reported by
   * the caller, because this function is pure and cannot report anything
   * itself. Absent config is NOT malformed - that case is normal and silent.
   */
  configMalformed?: boolean;
  /** The phase the decision was made under, 1 or 2. See `resolveReviewPromptPhase`. */
  phase?: number;
  /** True when a phase value was configured but could not be read. Same idea as `configMalformed`. */
  phaseMalformed?: boolean;
  /**
   * Which tenure rule was satisfied. Present exactly when `mayPrompt` is true.
   * When both rules pass, this is the EIGHT-week one, so the same person is
   * always counted the same way whichever test happened to run.
   */
  tenureRule?: TenureRule;
}

/**
 * How many workouts were completed in each of the recent whole weeks, most
 * recent week FIRST, in the person's OWN local weeks rather than UTC ones.
 *
 * Counts rather than booleans, deliberately. Phase 1 asks "at least 5 of the
 * last 8 weeks" and phase 2 will ask "3 of the last 4", and both come out of
 * the same single read without touching the helper that produced it.
 *
 * A `weeklyCounts` of `undefined` means the read FAILED, which is a third
 * answer and not zero. That field is the ONLY one carrying that meaning - see
 * `daysSinceLastWorkout` below, where absent means something different.
 */
export interface WorkoutHistory {
  /** Per-week completed-workout counts, most recent first. Absent when unreadable. */
  weeklyCounts?: number[];
  /**
   * Days since the most recent completed workout, or absent when there is none
   * in the window.
   *
   * **Absent never means "unreadable".** `weeklyCounts` carries that case, and
   * it is the ONLY field that does. If a failed read left this undefined
   * instead, the gate would refuse with `NoRecentWorkout` — a chosen refusal —
   * where it should refuse with `EngagementUnknown`, and the cannot-tell versus
   * logged-nothing distinction this whole shape exists for would be lost.
   */
  daysSinceLastWorkout?: number;
}

/** Everything the gate needs. Nothing here is read from a service; callers gather it. */
export interface ReviewPromptInputs {
  /** The rollout percent as configured, unvalidated. See `resolveRolloutPercent`. */
  configuredRolloutPercent: unknown;
  /** This person's stable 0-99 bucket. */
  bucket: number;
  /** True while a coach is managing somebody else's account. */
  isCoachViewingClient: boolean;
  /** True once anything has gone wrong in this session. */
  somethingWentWrongThisSession: boolean;
  /** When they last told us something was wrong, if they have. */
  lastReportedAProblem?: DateTime;
  /** When we last asked them to rate the app, if we have. */
  lastAskedToRate?: DateTime;
  /**
   * Every ask we know of, the last one included (#4816). Read only at phase 2,
   * where up to two asks a year are allowed. Absent is the same as none.
   */
  asksToRate?: DateTime[];
  /** The phase as configured, unvalidated. See `resolveReviewPromptPhase`. */
  configuredPhase?: unknown;
  /**
   * Do we know anything about their subscription at all?
   *
   * False on a warm resume or an offline start, where the CouchDB login result
   * every subscription field below is computed from has not arrived yet. Those
   * fields are then all `false`, which is indistinguishable from a real answer
   * — so this flag is what carries the difference, the way an absent
   * `weeklyCounts` carries it for the workout history.
   *
   * **Required, not optional, on purpose.** This package compiles without
   * `strictNullChecks`, so a caller that forgets it hands over `undefined`,
   * which is falsy and therefore refuses — the safe direction — while the
   * compiler still fails the build on the omission rather than letting an
   * unknown subscription pass as a known one.
   */
  hasSubscriptionFacts: boolean;
  /** Do they currently have paid access, by the server's own reckoning? */
  hasPaidAccess: boolean;
  /** Are they inside a free trial right now? */
  isInFreeTrial: boolean;
  /** Is the subscription cleanly active, as opposed to suspended / paused / cancelled? */
  isSubscriptionCleanlyActive: boolean;
  /** The first time they ever paid us, from the account record. Absent when unknown. */
  firstPaidAt?: DateTime;
  /** The workout history summary, which may say "cannot tell". */
  workoutHistory: WorkoutHistory;
  /** Now. */
  now: DateTime;
}

/** What the configured rollout value resolved to, and whether it was usable. */
export interface RolloutPercent {
  /** The percent to apply. Always usable, whatever was configured. */
  percent: number;
  /**
   * True when a value was present but could not be read. Distinct from absent,
   * which is normal and silent. A caller should report this: it means somebody
   * configured something that does not mean what they think it means.
   */
  malformed: boolean;
}

/** Phase 1's thresholds. Phase 2 widens these rather than replacing the gate. */
export const REVIEW_PROMPT_RULES = {
  /** How long somebody must have been paying before we ask. */
  minPayingWeeks: 8,
  /** How many of the recent weeks must contain a completed workout. */
  minTrainingWeeks: 5,
  /** How many recent weeks to look at. */
  trainingWindowWeeks: 8,
  /** How recently they must have trained. */
  maxDaysSinceLastWorkout: 10,
  /** How long before we may ask the same person again. */
  capDays: 365,
  /** How long a reported problem keeps us quiet. */
  problemQuietDays: 14,
  /** Phase 2's earlier tenure rule (#4816): paying this long ... */
  phase2MinPayingWeeks: 4,
  /** ... and trained in this many ... */
  phase2MinTrainingWeeks: 3,
  /** ... of this many recent weeks. */
  phase2TrainingWindowWeeks: 4,
  /** How many asks phase 2 allows inside one cap window. Apple allows 3; one is kept in reserve. */
  phase2MaxAsksPerCap: 2,
  /** The shortest gap between two asks at phase 2. */
  phase2MinDaysBetweenAsks: 90,
} as const;

/** What the configured phase resolved to, and whether it was usable. */
export interface ReviewPromptPhase {
  /** 1 (phase 1's narrow audience) or 2 (#4816's wider one). */
  phase: number;
  /** True when a value was present but could not be read. Absent is normal and silent. */
  malformed: boolean;
}

/**
 * Turn the configured phase into 1 or 2 (#4816).
 *
 * **This fails CLOSED, the opposite of `resolveRolloutPercent` right above it in
 * this feature — do not copy that one.** The rollout percent is an emergency
 * stop for a feature that ships on, so an unreadable value must not switch it
 * off. The phase is how WIDE the audience is, and the failure that matters is
 * widening it by accident, which the stores treat as a sudden burst of asks.
 * So only the exact number 2 means phase 2. Everything else — absent, a string
 * like `"2"` or `"two"`, 1.5, 3 — is phase 1.
 */
export function resolveReviewPromptPhase(configured: unknown): ReviewPromptPhase {
  // nothing configured is the normal case, and means phase 1
  if (configured === undefined || configured === null) return { phase: 1, malformed: false };
  if (configured === 1 || configured === 2) return { phase: configured, malformed: false };
  // a present value that is not exactly 1 or 2 narrows to phase 1 and is reported
  return { phase: 1, malformed: true };
}

/**
 * Turn the configured value into a usable percent.
 *
 * **This is the ONE place in this feature that fails OPEN**, and it is
 * deliberate. The configuration read returns nothing both when the key is
 * unset and when the configuration database cannot be reached, and the code
 * cannot tell those apart. This feature ships ON, so treating nothing as zero
 * would let a configuration outage silently switch it off — and a feature that
 * is not firing looks exactly like a feature with no eligible audience, so
 * nobody would notice. Only an explicit, readable number below 100 narrows it.
 *
 * See design-docs/review-prompts.md, "The switch".
 */
export function resolveRolloutPercent(configured: unknown): RolloutPercent {
  // Anything that is blank once trimmed means "nothing was configured", and it
  // is handled HERE, before any conversion. Number() turns '', '   ', '\n' and
  // even [] into 0, so letting any of them fall through would make an absent
  // value switch the feature OFF for everybody - the precise opposite of what
  // this function exists for, and invisible in production.
  if (configured === undefined || configured === null) return { percent: 100, malformed: false };
  if (typeof configured !== 'boolean' && String(configured).trim() === '') {
    return { percent: 100, malformed: false };
  }

  // a boolean is read as the on/off it plainly means. Number(true) is 1, which
  // would otherwise narrow the feature to a single percent of people and look
  // like a rollout somebody had chosen.
  if (typeof configured === 'boolean') return { percent: configured ? 100 : 0, malformed: false };

  const asNumber = typeof configured === 'number' ? configured : Number(configured);
  // a value that is present but unusable is NOT the same as an absent one, and
  // it is reported rather than silently coerced. An operator who mistypes -1 or
  // 1000 while narrowing the ramp in an incident would otherwise get a silent
  // 100% rollout with nothing saying so. Same shape as `clampRolloutPercent` in
  // the Paddle rollout config, whose caller reports its `malformed` flag.
  if (!Number.isFinite(asNumber)) return { percent: 100, malformed: true };
  if (asNumber < 0 || asNumber > 100) return { percent: 100, malformed: true };
  return { percent: asNumber, malformed: false };
}

/**
 * True when the date is usable. Luxon returns an INVALID DateTime rather than
 * throwing, and `now.diff(invalid).days` is NaN — so every `NaN < threshold`
 * comparison is false and the rule it guards silently stops applying.
 *
 * Used for the clock and for the tenure date, which are not windows since a
 * past moment. The two rules that ARE windows — the cap and the quiet period —
 * ask `mayHappenAgain` in `periodic-suppression.ts` instead, and pass
 * `WhenMomentUnreadable.Suppress` to get this same refusal.
 */
function isUsable(when: DateTime | undefined): boolean {
  // `!when` rather than `=== undefined`: this package compiles without
  // strictNullChecks, and a login-payload field absent from JSON arrives as
  // null, which would otherwise reach Luxon and throw.
  return !!when && when.isValid;
}

/**
 * Decide whether this is a person we may ask to rate the app.
 *
 * The order of the checks is chosen so the reported reason is the most useful
 * one: the things that are true of the whole session come first, then the
 * things about this person, then the things that needed a database read. A
 * caller that wants to know "why did nobody get prompted today?" gets a more
 * specific answer this way than if the cheapest check happened to win.
 */
export function decideReviewPrompt(inputs: ReviewPromptInputs): ReviewPromptDecision {
  // resolved FIRST, before any refusal can return. It is pure, so computing it
  // up here costs nothing - and every refusal then carries the malformed signal
  // rather than a few early ones claiming the config is fine on a path that
  // never read it. The went-wrong refusal is the likeliest of those during an
  // incident, which is exactly when somebody has just mistyped the ramp.
  const rollout = resolveRolloutPercent(inputs.configuredRolloutPercent);
  // the phase is resolved up here for the same reason: every answer carries it
  const { phase, malformed: phaseMalformed } = resolveReviewPromptPhase(inputs.configuredPhase);
  const no = (reason: NoPromptReason): ReviewPromptDecision =>
    ({ mayPrompt: false, reason, configMalformed: rollout.malformed, phase, phaseMalformed });

  // a coach looking at somebody else's account must never be asked to rate the
  // app on the strength of that person's data. First, because it is true
  // regardless of everything below it.
  if (inputs.isCoachViewingClient) return no(NoPromptReason.CoachViewingClient);

  // if anything went wrong in this session, this is the wrong moment whoever
  // they are. Somebody whose app just misbehaved is the last person who should
  // be handed a star rating, whether or not they noticed.
  if (inputs.somethingWentWrongThisSession) {
    return no(NoPromptReason.SomethingWentWrongThisSession);
  }

  // if we cannot even tell what time it is, nothing below can be judged
  if (!isUsable(inputs.now)) return no(NoPromptReason.ClockUnknown);
  // an uncomputable bucket refuses. `NaN < percent` is false, so without this
  // an unknown bucket would pass the rollout check rather than fail it.
  if (!Number.isFinite(inputs.bucket) || rollout.percent <= inputs.bucket) {
    return no(NoPromptReason.OutsideRollout);
  }

  // if they told us something was wrong recently, leave them alone. An UNUSABLE
  // date refuses rather than passing, which is what `Suppress` asks for: a
  // corrupt value must not silently switch the quiet period off.
  if (!mayHappenAgain(
    inputs.lastReportedAProblem,
    { days: REVIEW_PROMPT_RULES.problemQuietDays },
    inputs.now,
    WhenMomentUnreadable.Suppress,
  )) {
    return no(NoPromptReason.RecentlyReportedAProblem);
  }

  // if we have asked them within the cap window, do not ask again. This is the
  // most costly one to get wrong: the marker is read off a SYNCED document,
  // where a hand-edited or corrupted string really can appear, and an unusable
  // date that passed would switch the once-a-year cap off entirely — so this
  // one asks to be suppressed on an unusable date too.
  // phase 2 allows two asks a year, 90 days apart (#4816); phase 1 allows one
  const mayAskAgain = phase < 2
    ? mayHappenAgain(
      inputs.lastAskedToRate,
      { days: REVIEW_PROMPT_RULES.capDays },
      inputs.now,
      WhenMomentUnreadable.Suppress,
    )
    : mayAskAgainAtPhase2(inputs);
  if (!mayAskAgain) return no(NoPromptReason.AlreadyAskedRecently);

  // the subscription facts, which have three possible answers rather than two.
  // This one comes FIRST, before any of them is read: with no login result they
  // are all `false`, so every check below would refuse for a reason that claims
  // something about the person instead of admitting we have not looked.
  if (!inputs.hasSubscriptionFacts) return no(NoPromptReason.SubscriptionUnknown);

  if (!inputs.hasPaidAccess) return no(NoPromptReason.NotPaying);
  if (inputs.isInFreeTrial) return no(NoPromptReason.InFreeTrial);
  if (!inputs.isSubscriptionCleanlyActive) {
    return no(NoPromptReason.SubscriptionNotCleanlyActive);
  }

  // tenure. No date means we cannot judge it, and that fails CLOSED - the
  // opposite direction from the rollout percent above, because here an unknown
  // is about one person rather than about the whole feature.
  // an unusable date is as unknown as an absent one, and both refuse
  if (!isUsable(inputs.firstPaidAt)) return no(NoPromptReason.TenureUnknown);
  const payingWeeks = inputs.now.diff(inputs.firstPaidAt as DateTime, 'weeks').weeks;
  // phase 2 lets somebody in at four weeks; phase 1 needs eight
  const minPayingWeeks = phase >= 2
    ? REVIEW_PROMPT_RULES.phase2MinPayingWeeks
    : REVIEW_PROMPT_RULES.minPayingWeeks;
  if (payingWeeks < minPayingWeeks) {
    return no(NoPromptReason.TooNewlyPaying);
  }

  // the workout history, which has three possible answers rather than two
  const { weeklyCounts, daysSinceLastWorkout } = inputs.workoutHistory;
  if (weeklyCounts === undefined) return no(NoPromptReason.EngagementUnknown);

  const weeksTrainedIn = (windowWeeks: number): number => weeklyCounts
    .slice(0, windowWeeks)
    .filter(count => count > 0)
    .length;

  // the two tenure rules STACK. The eight-week rule is tried first, so somebody
  // who passes both is always recorded under it — without a fixed precedence
  // the two groups could not be compared later.
  let tenureRule: TenureRule;
  if (payingWeeks >= REVIEW_PROMPT_RULES.minPayingWeeks
    && weeksTrainedIn(REVIEW_PROMPT_RULES.trainingWindowWeeks) >= REVIEW_PROMPT_RULES.minTrainingWeeks) {
    tenureRule = TenureRule.EightWeek;
  } else if (phase >= 2
    && weeksTrainedIn(REVIEW_PROMPT_RULES.phase2TrainingWindowWeeks) >= REVIEW_PROMPT_RULES.phase2MinTrainingWeeks) {
    // paying long enough for this rule was already checked above
    tenureRule = TenureRule.FourWeek;
  } else {
    return no(NoPromptReason.NotTrainingConsistently);
  }

  // "trained lately" is weaker than it looks and is not a real activity signal:
  // there is no last-opened timestamp anywhere in the app, so this measures
  // training rather than opening. It is kept because it costs nothing on top of
  // the read above and it does exclude somebody who trains once after months
  // away. Absent means no workout was found in the window at all.
  // `!Number.isFinite` rather than `=== undefined`: this is the same NaN shape
  // guarded on every date above and on the bucket. NaN is neither undefined nor
  // greater than the threshold, so an unusable value would sail through this
  // check and PROMPT. The step-6 read computes this from a stored workout date,
  // which is precisely where an unusable one comes from.
  if (!Number.isFinite(daysSinceLastWorkout)
    || (daysSinceLastWorkout as number) > REVIEW_PROMPT_RULES.maxDaysSinceLastWorkout) {
    return no(NoPromptReason.NoRecentWorkout);
  }

  return { mayPrompt: true, configMalformed: rollout.malformed, phase, phaseMalformed, tenureRule };
}

/**
 * Phase 2's cap (#4816): at most two asks inside one cap window, and never two
 * within 90 days of each other.
 *
 * Both rules suppress on a date they cannot read, the same choice phase 1's cap
 * makes: an unusable date that passed would switch the cap off.
 */
function mayAskAgainAtPhase2(inputs: ReviewPromptInputs): boolean {
  // the spacing rule: the most recent ask must be far enough back
  if (!mayHappenAgain(
    inputs.lastAskedToRate,
    { days: REVIEW_PROMPT_RULES.phase2MinDaysBetweenAsks },
    inputs.now,
    WhenMomentUnreadable.Suppress,
  )) {
    return false;
  }

  // every known ask, each counted once. The last ask is normally in the list
  // too, so the two are merged by their exact moment.
  const asks = new Map<string, DateTime>();
  for (const ask of [...(inputs.asksToRate || []), inputs.lastAskedToRate]) {
    // if the moment is absent it is not an ask at all
    if (ask === undefined || ask === null) continue;
    asks.set(ask.isValid ? String(ask.toMillis()) : `invalid-${asks.size}`, ask);
  }

  let asksInsideCap = 0;
  for (const ask of asks.values()) {
    // if an ask is unreadable or inside the window, it counts against the cap
    if (!mayHappenAgain(ask, { days: REVIEW_PROMPT_RULES.capDays }, inputs.now, WhenMomentUnreadable.Suppress)) {
      asksInsideCap++;
    }
  }
  return asksInsideCap < REVIEW_PROMPT_RULES.phase2MaxAsksPerCap;
}
